/**
 * Swamithra — vetting requests.
 *
 * A vetting request is a notice plus the user's draft reply, reviewed by an
 * Expert. It shares the four-state enum with queries and, now, the thread
 * mechanics too: both the taxpayer and any Expert can post multiple messages,
 * the same shape as a query's conversation.
 *
 * THE FEEDBACK IS ABSENT, NOT EMPTY, BEFORE IT IS POSTED. US-00-004.11 requires
 * that an Open or In Progress request expose no feedback content at all — so the
 * field is nullable and the server sends null, rather than the client being
 * trusted to hide a value it was given.
 */

import { z } from "zod"

import { attachmentSchema } from "./attachment"
import { queryCategorySchema } from "./categories"
import {
	FEEDBACK_COMMENT_MAX_LENGTH,
	QUERY_TITLE_MAX_LENGTH,
	VETTING_NOTES_MAX_LENGTH,
} from "./constants"
import { feedbackRatingSchema, senderTypeSchema, workStatusSchema } from "./enums"
import { withReference } from "./ids"
import { optionalResponseDraftSchema } from "./response-draft"
import { richTextSchema, visibleRichTextSchema } from "./rich-text"

/** As in ./query: an instant the service sends null, or omits, until it happens. */
const nullableInstant = z
	.string()
	.nullish()
	.transform(value => value ?? null)

/** Free text the service sends as `""` rather than omitting. */
const optionalText = z
	.string()
	.nullish()
	.transform(value => (value === undefined || value === null || value === "" ? null : value))

/** A document slot that is null on a record whose upload did not resolve. */
const optionalDocument = attachmentSchema.nullish().transform(value => value ?? null)

// -----------------------------------------------------------------------------
// The user's view
// -----------------------------------------------------------------------------

const vettingSummaryFieldsSchema = z.object({
	id: z.string(),
	/** As the service rendered it — `#V12`. */
	reference: z.string(),
	/** The number inside the reference, derived by `withReference`. */
	sequence: z.number().int().positive(),
	title: z.string(),
	category: queryCategorySchema,
	status: workStatusSchema,
	submittedAt: z.string(),
	reviewedAt: nullableInstant,
	closedAt: nullableInstant,
	/** Activity this viewer's side has not opened (NOTASK-1). Older servers omit it. */
	hasUnseen: z.boolean().default(false),
	/** How many changes this viewer's side has not opened; every change counts. */
	unseenCount: z.number().int().nonnegative().default(0),
})

export const vettingSummarySchema = z.preprocess(withReference, vettingSummaryFieldsSchema)

export type VettingSummary = z.output<typeof vettingSummarySchema>

/**
 * A vetting rating already given.
 *
 * Same wire shape as query feedback — the service stamps `created_at` and
 * sends an empty string for "no comment"; this package calls the instant
 * `submittedAt` and treats an absent comment as null so a read-only summary
 * that renders an empty paragraph is not the same thing as one that renders
 * nothing.
 */
export const vettingFeedbackSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		return {
			...record,
			submittedAt: record.submittedAt ?? record.createdAt,
			comment: record.comment === "" ? null : record.comment,
		}
	},
	z.object({
		rating: feedbackRatingSchema,
		comment: z
			.string()
			.nullish()
			.transform(value => value ?? null),
		submittedAt: z.string(),
	}),
)

export type VettingFeedback = z.output<typeof vettingFeedbackSchema>

/**
 * The 'Was this helpful?' submission for a vetting request. Mirrors
 * queryFeedbackInputSchema — the same comment length rule, the same
 * three-layer enforcement (counter, schema, server 422).
 */
export const vettingFeedbackInputSchema = z.object({
	rating: feedbackRatingSchema,
	comment: z
		.string()
		.trim()
		.max(
			FEEDBACK_COMMENT_MAX_LENGTH,
			`Keep your comment under ${FEEDBACK_COMMENT_MAX_LENGTH} characters.`,
		)
		.optional(),
})

export type VettingFeedbackInput = z.infer<typeof vettingFeedbackInputSchema>

/**
 * One message in a vetting thread, from either side. Same wire shape as
 * queryMessageSchema — see its doc comment for why `senderName` is always
 * absent, and why one representation covers both a user's plain-text
 * follow-up and an Expert's rich-text reply.
 *
 * Attachments use the same wire shape as query-message attachments.
 */
export const vettingMessageSchema = z.object({
	id: z.string(),
	senderType: senderTypeSchema,
	senderName: z
		.string()
		.nullish()
		.transform(value => value ?? null),
	bodyRich: z.string(),
	attachments: z.array(attachmentSchema).default([]),
	createdAt: z.string(),
})

export type VettingMessage = z.infer<typeof vettingMessageSchema>

export const vettingDetailSchema = z.preprocess(
	withReference,
	vettingSummaryFieldsSchema.extend({
		notes: optionalText,
		notice: optionalDocument,
		draft: optionalDocument,
		/** The thread, chronological, empty until an Expert has opened it. */
		messages: z.array(vettingMessageSchema).default([]),
		/** The server's own answer to "show the awaiting-review placeholder?". */
		awaitingReview: z.boolean().default(false),
		/** The rating, or null while none has been submitted. */
		feedback: vettingFeedbackSchema.nullish().transform(value => value ?? null),
		/**
		 * The server's own answer to "show the 'Was this helpful?' prompt?":
		 * true when the request is Answered and no rating exists yet. Kept
		 * server-authoritative for the same reason as queryDetail.feedbackDue —
		 * three copies of "status === answered && feedback === null" is three
		 * chances to disagree with the 409 the API returns on a double submit.
		 */
		feedbackDue: z.boolean().default(false),
		/** False on a Closed request, which is what hides the composer. */
		canFollowUp: z.boolean().default(false),
	}),
)

export type VettingDetail = z.output<typeof vettingDetailSchema>

// -----------------------------------------------------------------------------
// The Expert's view
// -----------------------------------------------------------------------------

/**
 * A Vetting tab row.
 *
 * `userName` is empty on the response to a posted review — the service answers
 * that with the request's own fields and no user attached — so it defaults
 * rather than being required, exactly as on the panel's query row.
 */
export const expertVettingRowSchema = z.preprocess(
	withReference,
	vettingSummaryFieldsSchema.extend({
		/**
		 * The owner's own count (`#V3`), the number the user app shows and the
		 * user quotes on a call. Unique only per user: `reference` stays the
		 * panel's id.
		 */
		userReference: z.string().default(""),
		userName: z.string().default(""),
	}),
)

export type ExpertVettingRow = z.output<typeof expertVettingRowSchema>

export const expertVettingDetailSchema = z.preprocess(
	withReference,
	vettingSummaryFieldsSchema.extend({
		/**
		 * The owner's own count (`#V3`), the number the user app shows and the
		 * user quotes on a call. Unique only per user: `reference` stays the
		 * panel's id.
		 */
		userReference: z.string().default(""),
		userName: z.string().default(""),
		notes: optionalText,
		notice: optionalDocument,
		draft: optionalDocument,
		/** Both uploads in one array, in the order the panel shows them. */
		documents: z.array(attachmentSchema).default([]),
		/** The thread, chronological, empty until an Expert has opened it. */
		messages: z.array(vettingMessageSchema).default([]),
		/**
		 * The Expert who first opened it, recorded on that open (US-01-004.5).
		 *
		 * An ID, which is all the service sends. There is no name: the panel has
		 * no route that resolves one, so a display line is shown with its
		 * timestamp and, where a name is available to the caller, that.
		 */
		reviewedBy: optionalText,
		reviewedByName: optionalText,
		/** The rating, or null while none has been submitted. */
		feedback: vettingFeedbackSchema.nullish().transform(value => value ?? null),
		/** The Experts' saved, unsent reply, or null. Not `draft` above. */
		responseDraft: optionalResponseDraftSchema,
	}),
)

export type ExpertVettingDetail = z.output<typeof expertVettingDetailSchema>

// -----------------------------------------------------------------------------
// Inputs
// -----------------------------------------------------------------------------

/**
 * The text half of a create request. The notice and the draft are files and are
 * validated by ../files, which owns the per-slot type rules — the notice accepts
 * PDF/JPG/PNG, the draft additionally accepts DOCX (US-00-004.3).
 */
export const createVettingInputSchema = z.object({
	title: z
		.string()
		.trim()
		.min(1, "Give your request a title or reference.")
		.max(QUERY_TITLE_MAX_LENGTH, "That title is too long."),
	/**
	 * Rich-text markup, same contract as `reviewBodyRich` below — optional
	 * context rather than required content, so unlike `richTextSchema` this
	 * stays valid when omitted entirely. Not `.trim()`-ed: trimming a markup
	 * string risks clipping at a tag boundary. The caller omits the key rather
	 * than sending empty markup (see `isRichTextEmpty`).
	 */
	notes: visibleRichTextSchema(
		VETTING_NOTES_MAX_LENGTH,
		`Keep your notes to ${VETTING_NOTES_MAX_LENGTH} characters or fewer.`,
	).optional(),
})

export type CreateVettingInput = z.infer<typeof createVettingInputSchema>

/**
 * The Expert's message. Empty is blocked here as well as server-side
 * (US-01-004.8). Mirrors expertResponseInputSchema for queries.
 */
export const vettingMessageInputSchema = z.object({
	bodyRich: richTextSchema,
})

export type VettingMessageInput = z.infer<typeof vettingMessageInputSchema>

/**
 * A user's follow-up. Rich-text markup, same contract as a query follow-up —
 * mirrors followUpInputSchema for queries.
 */
export const postVettingMessageInputSchema = z.object({
	body: richTextSchema,
})

export type PostVettingMessageInput = z.infer<typeof postVettingMessageInputSchema>

/**
 * The Vetting tab offers a status filter and NOTHING ELSE — no search, no
 * category filter, no date range, no SLA column (US-01-004.3). The absence is
 * specified, so the type says so.
 */
export type ExpertVettingFilters = {
	status?: z.infer<typeof workStatusSchema>
}

export type MyVettingFilters = {
	status?: z.infer<typeof workStatusSchema>
}
